import { Factory } from 'fishery';
import faker from 'faker';
import { PaymentFrequencyEnum, PaymentMethodEnum } from '.';
import {
  PaymentPlanType,
  PaymentDetailsResponseType,
  PaymentFrequencyType,
  PaymentScheduleType,
} from './types';
import { feeStrategyFactory } from '../FeeStrategy/factory';

type PaymentPlanTransientParams = {
  identifier: string;
  frequency: PaymentFrequencyEnum;
  method: PaymentMethodEnum;
  hasDiscount?: boolean;
};

export const paymentPlanFactory = Factory.define<
  PaymentPlanType,
  PaymentPlanTransientParams
>(({ transientParams }) => {
  const method = transientParams.method || 'credit_card';
  const frequency = transientParams.frequency || 'monthly';
  return {
    identifier: transientParams.identifier || `${method}_${frequency}`,
    paymentMethodIdentifier: method,
    paymentFrequencyIdentifier: frequency,
    downPaymentAmount: '1',
    additionalDown: false,
    totalAmount: '2',
    totalPremiumAmount: '2',
    totalPremiumAndTaxAmount: '12',
    totalFeeAmount: '1',
    totalTaxAmount: '3',
    totalTaxAndFeeAmount: '4',
    installmentAmount: '0',
    installmentPremiumAmount: '0',
    monthlyAvgPremiumAndTaxAmount: '6',
    installmentFeeAmount: '0',
    paymentSchedule: [
      {
        date: '10/10/2001',
        amount: '$20',
        items: [],
        type: 'DOWNPAYMENT',
      },
      {
        date: '11/10/2001',
        amount: '$60',
        items: [],
        type: 'INSTALLMENT',
      },
    ],
    messages: [],
    discounts: transientParams.hasDiscount
      ? [{ type: 'paymentFrequencyDiscount', amount: 15, subtype: 'PERCENT' }]
      : [],
    paymentFrequencyLabel: 'Monthly payments',
    paymentPlanEnum: 'monthly_five_payments_cc',
    downPaymentBreakdown: [
      {
        amount: 21,
        formattedAmount: '$21.00',
        name: 'Policy Premium',
      },
    ],
    totalAmountBreakdown: [
      {
        amount: 21,
        formattedAmount: '$21.00',
        name: 'Policy Premium',
      },
    ],
    paymentDaysOfMonth: [],
    paymentDaysOfWeek: [],
    paymentFrequencyIdentifierOptions: [],
    feeStrategy: feeStrategyFactory.build(undefined, {
      transient: { kind: 'variable', title: 'Variable fees', fees: [] },
    }),
  };
});

type PaymentConfigTransientParams = {
  paymentFrequencies: PaymentFrequencyType[];
};

export const paymentDetailsFactory = Factory.define<
  PaymentDetailsResponseType,
  PaymentConfigTransientParams
>(({ transientParams }) => {
  return {
    paymentDetails: {
      attributes: {
        defaultPaymentPlan: { identifier: 'credit_card_monthly' },
        messages: [],
        paymentFrequencies: transientParams.paymentFrequencies || [
          {
            identifier: 'monthly',
            numberOfPayments: 6,
            label: 'Monthly payments',
            description: '6 monthly payments',
            type: 'equal',
            paymentPlanEnum: 'monthly_six_payments_cc',
            paymentPlanId: 'credit_card_monthly',
          },
          {
            identifier: 'in_full',
            numberOfPayments: 1,
            label: 'Pay in full',
            description: 'Pay entire premium today',
            type: 'equal',
            paymentPlanEnum: 'full_pay_cc',
            paymentPlanId: 'credit_card_in_full',
          },
        ],
        paymentMethods: [
          {
            identifier: 'credit_card',
            label: 'Credit Card',
          },
        ],
        paymentPlans: paymentPlansWithoutDiscount,
        paymentProvider: 'legacy',
        quoteId: 'string',
      },
    },
  };
});

export const paymentFrequencyFactory = Factory.define<PaymentFrequencyType>(
  () => {
    return {
      identifier: 'monthly',
      numberOfPayments: 6,
      label: 'Monthly payments',
      description: '6 monthly payments',
      type: 'equal',
      paymentPlanEnum: 'monthly_six_payments_cc',
      paymentPlanId: 'credit_card_monthly',
    };
  }
);

export const paymentPlansWithoutDiscount = [
  paymentPlanFactory
    .transient({ frequency: 'monthly', method: 'credit_card' })
    .build(),
  paymentPlanFactory
    .transient({ frequency: 'in_full', method: 'credit_card' })
    .build(),
];

export const paymentPlansWithDiscount = [
  paymentPlanFactory
    .transient({ frequency: 'monthly', method: 'credit_card' })
    .build(),
  paymentPlanFactory
    .transient({
      frequency: 'in_full',
      method: 'credit_card',
      hasDiscount: true,
    })
    .build(),
];

const paymentPlanDefaults: Record<string, Partial<PaymentPlanType>> = {
  monthlySixPayments: {
    totalAmount: '$1,767.57',
    totalFeeAmount: '$117.50',
    totalPremiumAmount: '$1,650.07',
    totalPremiumAndTaxAmount: '$1,650.07',
    paymentPlanEnum: 'monthly_equal_payment_early_installments_cc',
    paymentFrequencyLabel: 'Monthly payments',
    paymentFrequencyIdentifier: 'monthly',
    paymentSchedule: [
      { amount: '$347.52', date: '03/05/2025', items: [], type: 'DOWNPAYMENT' },
      { amount: '$284.01', date: '03/20/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$284.01', date: '03/20/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$284.01', date: '03/20/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$284.01', date: '03/20/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$284.01', date: '03/20/2025', items: [], type: 'INSTALLMENT' },
    ],
    downPaymentAmount: '$347.52',
    installmentAmount: '$284.01',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$275.01',
  },
  threePayments: {
    totalAmount: '$5,422.40',
    totalFeeAmount: '$90.50',
    totalPremiumAmount: '$5,331.90',
    totalPremiumAndTaxAmount: '$5,331.90',
    paymentPlanEnum: 'three_payments_cc',
    paymentFrequencyLabel: 'Three payments',
    paymentFrequencyIdentifier: 'monthly',
    paymentSchedule: [
      {
        amount: '$2,205.26',
        date: '01/17/2025',
        items: [],
        type: 'DOWNPAYMENT',
      },
      {
        amount: '$1,608.57',
        date: '02/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
      {
        amount: '$1,608.57',
        date: '03/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
    ],
    downPaymentAmount: '$2,205.26',
    installmentAmount: '$1,608.57',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$1,599.57',
  },
  monthlyFivePayments: {
    totalAmount: '$5,440.40',
    totalFeeAmount: '$108.50',
    totalPremiumAmount: '$5,331.90',
    totalPremiumAndTaxAmount: '$5,331.90',
    paymentPlanEnum: 'monthly_five_payments_cc',
    paymentFrequencyLabel: 'Monthly payments',
    paymentFrequencyIdentifier: 'monthly',
    paymentSchedule: [
      {
        amount: '$1,832.04',
        date: '01/17/2025',
        items: [],
        type: 'DOWNPAYMENT',
      },
      { amount: '$902.09', date: '02/18/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$902.09', date: '03/18/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$902.09', date: '04/18/2025', items: [], type: 'INSTALLMENT' },
      { amount: '$902.09', date: '05/18/2025', items: [], type: 'INSTALLMENT' },
    ],
    downPaymentAmount: '$1,832.04',
    installmentAmount: '$902.09',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$893.09',
  },
  fourPayments: {
    totalAmount: '$5,431.40',
    totalFeeAmount: '$99.50',
    totalPremiumAmount: '$5,331.90',
    totalPremiumAndTaxAmount: '$5,331.90',
    paymentPlanEnum: 'four_payments_cc',
    paymentFrequencyLabel: 'Four payments',
    paymentFrequencyIdentifier: 'monthly',
    paymentSchedule: [
      {
        amount: '$1,405.49',
        date: '01/17/2025',
        items: [],
        type: 'DOWNPAYMENT',
      },
      {
        amount: '$1,341.97',
        date: '02/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
      {
        amount: '$1,341.97',
        date: '03/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
      {
        amount: '$1,341.97',
        date: '04/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
    ],
    downPaymentAmount: '$1,405.49',
    installmentAmount: '$1,341.97',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$1,332.97',
  },
  preferredFivePayments: {
    totalAmount: '$5,440.40',
    totalFeeAmount: '$108.50',
    totalPremiumAmount: '$5,331.90',
    totalPremiumAndTaxAmount: '$5,331.90',
    paymentPlanEnum: 'preferred_five_payments_cc',
    paymentFrequencyLabel: 'Preferred Five payments',
    paymentFrequencyIdentifier: 'monthly',
    paymentSchedule: [
      {
        amount: '$1,405.49',
        date: '01/17/2025',
        items: [],
        type: 'DOWNPAYMENT',
      },
      {
        amount: '$1,008.73',
        date: '02/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
      {
        amount: '$1,008.73',
        date: '03/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
      {
        amount: '$1,008.73',
        date: '04/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
      {
        amount: '$1,008.73',
        date: '05/18/2025',
        items: [],
        type: 'INSTALLMENT',
      },
    ],
    downPaymentAmount: '$1,405.49',
    installmentAmount: '$1,008.73',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$999.73',
  },
  semiMonthly: {
    totalAmount: '$1,812.57',
    totalFeeAmount: '$162.50',
    totalPremiumAmount: '$1,650.07',
    totalPremiumAndTaxAmount: '$1,650.07',
    paymentPlanEnum: 'semi_monthly_equal_payment_cc',
    paymentFrequencyLabel: 'Eleven payments',
    paymentFrequencyIdentifier: 'semi_monthly',
    paymentSchedule: [
      { date: '03/05/2025', amount: '$347.57', items: [], type: 'DOWNPAYMENT' },
      { date: '03/20/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '04/06/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '04/20/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '05/06/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '05/20/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '06/06/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '06/20/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '07/06/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '07/20/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
      { date: '08/06/2025', amount: '$146.50', items: [], type: 'INSTALLMENT' },
    ],
    downPaymentAmount: '$347.57',
    installmentAmount: '$146.50',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$137.50',
  },
  inFull: {
    totalAmount: '$1,635.50',
    totalFeeAmount: '$72.50',
    totalPremiumAmount: '$1,563.00',
    totalPremiumAndTaxAmount: '$1,563.00',
    paymentPlanEnum: 'full_pay_cc',
    paymentFrequencyLabel: 'Pay in full',
    paymentFrequencyIdentifier: 'in_full',
    paymentSchedule: [
      {
        date: '03/05/2025',
        amount: '$1,635.50',
        items: [],
        type: 'DOWNPAYMENT',
      },
    ],
    downPaymentAmount: '$1,635.50',
    installmentAmount: '$1,635.50',
    installmentFeeAmount: '$9.00',
    installmentPremiumAmount: '$1,563.00',
  },
};

const createPaymentPlanByType = (
  key: keyof typeof paymentPlanDefaults,
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
): PaymentPlanType =>
  paymentPlanFactory.transient({ identifier }).build({
    ...paymentPlanDefaults[key],
    ...overrides,
  });

export const createMonthlySixPayments = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('monthlySixPayments', identifier, overrides);

export const createThreePayments = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('threePayments', identifier, overrides);

export const createMonthlyFivePayments = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('monthlyFivePayments', identifier, overrides);

export const createFourPayments = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('fourPayments', identifier, overrides);

export const createPreferredFivePayments = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('preferredFivePayments', identifier, overrides);

export const createSemiMonthlyPaymentPlan = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('semiMonthly', identifier, overrides);

export const createInFullPaymentPlan = (
  identifier?: string,
  overrides: Partial<PaymentPlanType> = {}
) => createPaymentPlanByType('inFull', identifier, overrides);

export const createMonthlyPaymentFrequency = (
  paymentPlanId?: string
): PaymentFrequencyType => ({
  identifier: 'monthly',
  numberOfPayments: 6,
  label: 'Monthly payments',
  description: '$347.52 down payment + 5 additional payments',
  type: 'equal',
  paymentPlanEnum: 'monthly_equal_payment_early_installments_cc',
  paymentPlanId: paymentPlanId ?? 'dbafadec-3f2e-4c6d-85e8-2f3082f84b6d',
});

export const createSemiMonthlyPaymentFrequency = (
  paymentPlanId?: string
): PaymentFrequencyType => ({
  identifier: 'semi_monthly',
  numberOfPayments: 11,
  label: 'Eleven payments',
  description: '$347.57 down payment + 10 additional payments',
  type: 'equal',
  paymentPlanEnum: 'semi_monthly_equal_payment_cc',
  paymentPlanId: paymentPlanId ?? '943ff28e-b987-45aa-bb15-ba6ba13e6a1f',
});

export const createInFullPaymentFrequency = (
  paymentPlanId?: string
): PaymentFrequencyType => ({
  identifier: 'in_full',
  numberOfPayments: 1,
  label: 'Pay in full',
  description: 'Pay entire total cost today',
  type: 'equal',
  paymentPlanEnum: 'full_pay_cc',
  paymentPlanId: paymentPlanId ?? '91ff1310-14ab-450b-bbc5-3f3ab6b4cd1c',
});

export const createThreePaymentFrequency = (
  paymentPlanId?: string
): PaymentFrequencyType => ({
  identifier: 'monthly',
  numberOfPayments: 3,
  label: 'Three payments',
  description: '$732.53 down payment + 2 additional payments',
  type: 'downPay',
  paymentPlanEnum: 'three_payments_cc',
  paymentPlanId: paymentPlanId ?? 'd506ba94-4d2a-4762-9414-109167236279',
});

export const createFourPaymentFrequency = (
  paymentPlanId?: string
): PaymentFrequencyType => ({
  identifier: 'monthly',
  numberOfPayments: 4,
  label: 'Four payments',
  description: '$485.04 down payment + 3 additional payments',
  type: 'equal',
  paymentPlanEnum: 'four_payments_cc',
  paymentPlanId: paymentPlanId ?? '68f8cf98-d09c-4ec2-b6fa-a02f10a7ba2d',
});

export const createFivePaymentFrequency = (
  paymentPlanId?: string
): PaymentFrequencyType => ({
  identifier: 'monthly',
  numberOfPayments: 5,
  label: 'Monthly payments',
  description: '$485.05 down payment + 4 additional payments',
  type: 'downPay',
  paymentPlanEnum: 'monthly_five_payments_cc',
  paymentPlanId: paymentPlanId ?? 'a67c1e3e-3018-44a9-b6df-addefebc1a9d',
});

export const createFlexibleMonthlyPaymentPlan = (
  overrides: Partial<PaymentPlanType> = {}
) => {
  return paymentPlanFactory.build({
    identifier: faker.datatype.uuid(),
    paymentPlanEnum: 'flexible',
    paymentFrequencyLabel: 'monthly',
    paymentMethodIdentifier: 'credit_card',
    paymentFrequencyIdentifier: 'monthly',
    additionalDown: false,
    paymentSchedule: [
      {
        date: '07/01/2025',
        amount: '$193.32',
        items: [{ name: 'policyFee', amount: 4.25 }],
        type: 'DOWNPAYMENT',
      },
      {
        date: '08/01/2025',
        amount: '$193.31',
        items: [
          { name: 'predictableProtection', amount: 50.0 },
          { name: 'policyFee', amount: 4.15 },
        ],
        type: 'INSTALLMENT',
      },
      {
        date: '09/01/2025',
        amount: '$193.31',
        items: [
          { name: 'predictableProtection', amount: 50.0 },
          { name: 'policyFee', amount: 4.15 },
        ],
        type: 'INSTALLMENT',
      },
      {
        date: '10/01/2025',
        amount: '$193.31',
        items: [
          { name: 'predictableProtection', amount: 50.0 },
          { name: 'policyFee', amount: 4.15 },
        ],
        type: 'INSTALLMENT',
      },
      {
        date: '11/01/2025',
        amount: '$193.31',
        items: [
          { name: 'predictableProtection', amount: 50.0 },
          { name: 'policyFee', amount: 4.15 },
        ],
        type: 'INSTALLMENT',
      },
      {
        date: '12/01/2025',
        amount: '$193.31',
        items: [
          { name: 'predictableProtection', amount: 50.0 },
          { name: 'policyFee', amount: 4.15 },
        ],
        type: 'INSTALLMENT',
      },
    ] as PaymentScheduleType[],
    messages: [],
    discounts: [],
    paymentFrequencyIdentifierOptions: [
      'weekly',
      'bi_weekly',
      'semi_monthly',
      'monthly',
      'full_pay',
    ],
    paymentDaysOfWeek: ['tuesday'],
    paymentDaysOfMonth: [1],
    downPaymentAmount: '$193.32',
    totalAmount: '$1,159.87',
    totalPremiumAmount: '$1,159.87',
    totalPremiumAndTaxAmount: '$1,159.87',
    totalFeeAmount: null,
    totalTaxAmount: null,
    totalTaxAndFeeAmount: null,
    installmentAmount: '$193.31',
    installmentPremiumAmount: '$193.31',
    monthlyAvgPremiumAndTaxAmount: '$193.31',
    installmentFeeAmount: '$0.00',
    ...overrides,
  });
};

export const createFlexibleInFullPaymentPlan = (
  overrides: Partial<PaymentPlanType> = {}
) => {
  return paymentPlanFactory.build({
    identifier: faker.datatype.uuid(),
    paymentPlanEnum: 'flexible',
    paymentFrequencyLabel: 'pay full amount today',
    paymentMethodIdentifier: 'credit_card',
    paymentFrequencyIdentifier: 'full_pay',
    additionalDown: false,
    paymentSchedule: [
      {
        date: '07/01/2025',
        amount: '$1,063.17',
        items: [
          {
            name: 'premium',
            type: 'PRODUCT',
            amount: 1013.17,
            subtype: 'PER_PRODUCT',
          },
          {
            name: 'policyFee',
            type: 'FEE',
            amount: 25,
            subtype: 'PER_PRODUCT',
          },
          {
            name: 'predictableProtection',
            type: 'FEE',
            amount: 120,
            subtype: 'PER_PRODUCT',
          },
        ],
        type: 'DOWNPAYMENT',
      },
    ],
    messages: [],
    discounts: [],
    paymentFrequencyIdentifierOptions: [
      'weekly',
      'bi_weekly',
      'semi_monthly',
      'monthly',
      'full_pay',
    ],
    paymentDaysOfWeek: ['tuesday'],
    paymentDaysOfMonth: [1],
    downPaymentAmount: '$1,063.17',
    totalAmount: '$1,063.17',
    totalPremiumAmount: '$1,063.17',
    totalPremiumAndTaxAmount: '$1,063.17',
    totalFeeAmount: null,
    totalTaxAmount: null,
    totalTaxAndFeeAmount: null,
    installmentAmount: '$0.00',
    installmentPremiumAmount: '$0.00',
    monthlyAvgPremiumAndTaxAmount: '$177.19',
    installmentFeeAmount: null,
    ...overrides,
  });
};
